import React from "react";
import { Form, Col, Tab, Card, Nav } from "react-bootstrap";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { withNavigation } from "../../utils/wrapper";
import ConditionalButton from "../../layout/conditional-button";
import { send as pageInfo, loading } from "../../actions/page";
import { get as getSettings, save as saveSetting } from "../../actions/settings";
import { send as alert } from "../../actions/alert";
import { get as getAddress, save as saveAddress } from "../../actions/addresses";
import Settings from "./settings";
import ConfirmModal from "../../layout/modal";
import Calendar from "../../layout/calendar";

class Firm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            firm: {},
            addresses: [],
            modal: {
                confirm: {
                    show: false
                }
            },
            changed: false,
            selected: "profile"
        };

        this.loadingRef = false;
        this.props.pageInfo({ title: "Firm Information", breadcrumb: "Firm Information" });
    }

    componentDidMount = () => {
        // If this was intentionally delayed for layout timing, keep it.
        setTimeout(async () => {
            await this.loadData();
        }, 30);
    };

    safeParseLocations = (value) => {
        try {
            if (!value) return [];
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    };

    loadData = async () => {
        if (this.loadingRef) return;
        this.loadingRef = true;
        this.props.loading(true);

        try {
            const firm = await this.props.getSettings({ authentic: true, category: "Firm" });
            const locationUuids = this.safeParseLocations(firm?.FIRM_LOCATIONS);

            const addresses = [];
            for (let i = 0; i < locationUuids.length; i++) {
                const uuid = locationUuids[i];
                if (!uuid) continue;
                const address = await this.props.getAddress({ uuid });
                if (address) addresses.push({ ...address });
            }

            this.setState({
                firm: firm || {},
                addresses
            });
        } catch (err) {
            this.props.alert(err?.message || "Unable to load firm information.", "danger");
        } finally {
            this.props.loading(false);
            this.loadingRef = false;
        }
    };

    onConfirmShow = (config) => {
        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                confirm: {
                    ...config,
                    show: true
                }
            }
        }));
    };

    onConfirmHideModal = () => {
        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                confirm: {
                    ...prev.modal.confirm,
                    show: false
                }
            }
        }));
    };

    onTabChange = (e, selected) => {
        e.preventDefault();
        this.setState({ selected });
    };

    onChange = (e) => {
        const { name, value } = e.target;
        this.setState((prev) => ({
            firm: {
                ...prev.firm,
                [name]: value
            },
            changed: true
        }));
    };

    onSubmit = (e) => {
        e.preventDefault();
    };

    doSaveAddresses = async () => {
        const addresses = this.state.addresses || [];
        for (let i = 0; i < addresses.length; i++) {
            const address = addresses[i];
            await this.props.saveAddress({ ...address });
        }
    };

    onSaveClick = async () => {
        this.props.loading(true);
        try {
            await this.doSaveAddresses();

            const entries = Object.entries(this.state.firm || {});
            for (let i = 0; i < entries.length; i++) {
                const [key, value] = entries[i];
                let saveValue = value;

                if (key === "FIRM_LOCATIONS") {
                    saveValue = JSON.stringify((this.state.addresses || []).map((o) => o?.uuid).filter(Boolean));
                }

                const setting = {
                    name: key,
                    category: "Firm",
                    value: saveValue
                };

                if (key === "FIRM_REVIEW_GOOGLE_URL") setting.visibility = 1;

                await this.props.saveSetting(setting);
            }

            // Ensure FIRM_LOCATIONS is persisted even if not already present in firm object
            if (!Object.prototype.hasOwnProperty.call(this.state.firm || {}, "FIRM_LOCATIONS")) {
                await this.props.saveSetting({
                    name: "FIRM_LOCATIONS",
                    category: "Firm",
                    value: JSON.stringify((this.state.addresses || []).map((o) => o?.uuid).filter(Boolean)),
                    visibility: 1
                });
            }

            this.setState({ changed: false });
            this.props.alert("Information saved", "success");
        } catch (err) {
            this.props.alert(err?.message || "Unable to save firm information.", "danger");
        } finally {
            this.props.loading(false);
        }
    };

    onAddressAdd = () => {
        this.setState((prev) => ({
            addresses: [...(prev.addresses || []), { uuid: uuidv4() }],
            changed: true
        }));
    };

    onAddressChange = (address, index) => {
        this.setState((prev) => ({
            addresses: (prev.addresses || []).map((item, i) => (i === index ? { ...address } : item)),
            changed: true
        }));
    };

    onAddressDelete = (address, index) => {
        this.onConfirmShow({
            title: "Delete address?",
            body: `Click ok to delete this address (${address?.line1 || "no line1"}).`,
            onOk: () => this.onConfirmDeleteAddress(index)
        });
    };

    onConfirmDeleteAddress = (index) => {
        this.onConfirmHideModal();
        this.setState((prev) => ({
            addresses: (prev.addresses || []).filter((_, i) => i !== index),
            changed: true
        }));
    };

    render() {
        return (
            <>
                <Col md="8" className="float-right text-end">
                    <ConditionalButton
                        enabledVariant="outline-primary"
                        tooltip="Save"
                        display={true}
                        enabled={true}
                        onEnabledClick={this.onSaveClick}
                        icon="save"
                    >
                        Save
                    </ConditionalButton>
                </Col>

                <Tab.Container activeKey={this.state.selected}>
                    <Card.Header className="app-tab-header border-0 pb-0">
                        <Nav
                            className="app-tab-nav nav-tabs-line card-header-tabs"
                            variant="tabs"
                            activeKey={this.state.selected}
                        >
                            <Nav.Item className="text-center">
                                <Nav.Link
                                    onClick={(event) => this.onTabChange(event, "profile")}
                                    className={`${this.state.selected === "profile" ? "active" : ""} app-tab-link`}
                                >
                                    Profile
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item className="text-center">
                                <Nav.Link
                                    className={`${this.state.selected === "calendar" ? "active" : ""} app-tab-link`}
                                    onClick={(event) => this.onTabChange(event, "calendar")}
                                >
                                    Calendar
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                    </Card.Header>

                    <Col xs="12">
                        <Form name="frm" action="post" onSubmit={this.onSubmit}>
                            <Tab.Content className="app-tab-content-min h-100">
                                <Tab.Pane active={this.state.selected === "profile"} className="h-100 scroll-out">
                                    <Settings
                                        onAddressDelete={this.onAddressDelete}
                                        onAddressChange={this.onAddressChange}
                                        onAddAddressClick={this.onAddressAdd}
                                        onChange={this.onChange}
                                        firm={this.state.firm}
                                        addresses={this.state.addresses}
                                    />
                                </Tab.Pane>

                                <Tab.Pane active={this.state.selected === "calendar"} className="h-100 scroll-out">
                                    <Calendar firm />
                                </Tab.Pane>
                            </Tab.Content>
                        </Form>
                    </Col>
                </Tab.Container>

                <ConfirmModal
                    {...this.state.modal.confirm}
                    buttons={[
                        {
                            caption: "ok",
                            onEnabledClick: this.state.modal.confirm.onOk,
                            variant: "secondary",
                            enabled: true,
                            display: true
                        },
                        {
                            default: true,
                            caption: "cancel",
                            onEnabledClick: this.onConfirmHideModal,
                            variant: "primary",
                            enabled: true,
                            display: true
                        }
                    ]}
                />
            </>
        );
    }
}

Firm.propTypes = {
    auth: PropTypes.object.isRequired,
    pageInfo: PropTypes.func.isRequired,
    loading: PropTypes.func.isRequired,
    getSettings: PropTypes.func.isRequired,
    saveSetting: PropTypes.func.isRequired,
    getAddress: PropTypes.func.isRequired,
    saveAddress: PropTypes.func.isRequired,
    alert: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default withNavigation(
    connect(mapStateToProps, {
        pageInfo,
        loading,
        getSettings,
        saveSetting,
        getAddress,
        saveAddress,
        alert
    })(Firm)
);
